import {
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  ArrowLeft,
  BadgeCheck,
  Bell,
  CalendarCheck2,
  Camera,
  Check,
  CircleAlert,
  Coins,
  Crown,
  Download,
  Eye,
  EyeOff,
  Gift,
  Headphones,
  History,
  Info,
  LoaderCircle,
  LockKeyhole,
  MessageSquare,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Ticket,
  Trophy,
  UserRound,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  engagementApi,
  type LotteryRecord,
  type PrizeRecord,
} from "../api/engagement";
import { formatUsd } from "../utils/money";
import { resolveMediaUrl } from "../api/marketplace";
import { useCopy } from "../i18n/copy";
import { useSession } from "../store/session";
import { RemoteNoticeDialog } from "../components/RemoteNoticeDialog";

function prizeLabel(prize: PrizeRecord, tr: (value: string) => string) {
  if (String(prize.type) === "2") return `${prize.count} ${tr("puntos")}`;
  if (String(prize.type) === "3") return `${formatUsd(Number(prize.count))}`;
  if (String(prize.type) === "4") return `${prize.count} ${tr("cupón")}`;
  return tr("Sigue participando");
}

function lotteryLabel(record: LotteryRecord, tr: (value: string) => string) {
  if (String(record.type) === "2") return `${record.count} ${tr("puntos")}`;
  if (String(record.type) === "3") return formatUsd(Number(record.count));
  if (String(record.type) === "4") return `${record.count} ${tr("cupón")}`;
  return tr("Sigue participando");
}

function PageHeader({
  eyebrow,
  title,
  action,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  const { tr } = useCopy();
  return (
    <>
      <Link className="back-link" to="/account">
        <ArrowLeft /> {tr("Volver a mi cuenta")}
      </Link>
      <header className="page-title-row engagement-title">
        <div>
          <p className="eyebrow">{tr(eyebrow)}</p>
          <h1>{tr(title)}</h1>
        </div>
        {action}
      </header>
    </>
  );
}

export function CheckinPage() {
  const { tr } = useCopy();
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["checkin"],
    queryFn: engagementApi.checkin,
  });
  const days = query.data?.list || [];
  const checked = days.filter((day) => day.isCheckIn).length;
  const earned = days
    .filter((day) => day.isCheckIn)
    .reduce((sum, day) => sum + Number(day.award), 0);
  const nextIndex = days.findIndex((day) => !day.isCheckIn);
  const doneToday =
    query.data?.btn === false ||
    Boolean(query.data?.isCheckIn) ||
    nextIndex < 0;
  const sign = useMutation({
    mutationFn: engagementApi.submitCheckin,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ["checkin"] });
      client.invalidateQueries({ queryKey: ["session-user"] });
    },
  });
  return (
    <main className="page engagement-page">
      <PageHeader
        eyebrow="Beneficios diarios"
        title="Asistencia diaria"
        description="Completa la secuencia y recibe puntos en cada jornada."
      />
      <section className="checkin-hero">
        <div>
          <span className="checkin-mark">
            <CalendarCheck2 />
          </span>
          <p>{tr("Progreso semanal")}</p>
          <strong>
            {checked}
            <small> / {days.length || 7}</small>
          </strong>
        </div>
        <div className="checkin-stats">
          <span>
            <small>{tr("Puntos recibidos")}</small>
            <b>{earned}</b>
          </span>
          <span>
            <small>{tr("Próxima recompensa")}</small>
            <b>{nextIndex >= 0 ? days[nextIndex]?.award : "—"}</b>
          </span>
        </div>
        <button
          className="button sun"
          disabled={doneToday || sign.isPending || query.isLoading}
          onClick={() => sign.mutate()}
        >
          {sign.isPending ? <LoaderCircle className="spin" /> : <Check />}{" "}
          {tr(doneToday ? "Completado hoy" : "Registrar asistencia")}
        </button>
      </section>
      {sign.isSuccess && (
        <div className="form-feedback success" role="status">
          <BadgeCheck /> {tr("Asistencia registrada correctamente.")}
        </div>
      )}
      {sign.isError && (
        <div className="form-feedback error" role="alert">
          <CircleAlert /> {sign.error.message}
        </div>
      )}
      <section className="checkin-board">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{tr("Esta semana")}</p>
            <h2>{tr("Recompensas por día")}</h2>
          </div>
          <span>{Math.round((checked / Math.max(days.length, 1)) * 100)}%</span>
        </div>
        {query.isLoading ? (
          <div className="loading-row">
            <LoaderCircle className="spin" /> {tr("Cargando…")}
          </div>
        ) : (
          <div className="checkin-grid">
            {days.map((day, index) => (
              <article
                className={`${day.isCheckIn ? "done" : ""} ${index === nextIndex ? "current" : ""}`}
                key={day.day}
              >
                <span>{day.isCheckIn ? <Check /> : index + 1}</span>
                <small>
                  {tr("Día")} {day.day}
                </small>
                <strong>+{day.award}</strong>
                <em>{tr("puntos")}</em>
              </article>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}

export function MessagesPage() {
  const { tr } = useCopy();
  const query = useQuery({
    queryKey: ["messages"],
    queryFn: engagementApi.messages,
  });
  return (
    <main className="page engagement-page">
      <PageHeader
        eyebrow="Centro de cuenta"
        title="Mensajes"
        description="Consulta avisos y novedades relacionados con tu cuenta."
        action={
          <span className="title-icon">
            <Bell />
          </span>
        }
      />
      {query.isLoading && (
        <div className="loading-row">
          <LoaderCircle className="spin" /> {tr("Cargando mensajes…")}
        </div>
      )}
      {query.isError && (
        <div className="form-feedback error">
          <CircleAlert /> {tr("No pudimos cargar los mensajes.")}
        </div>
      )}
      <section className="message-list">
        {query.data?.map((message, index) => (
          <article key={message.id || index}>
            <span>
              <MessageSquare />
            </span>
            <div>
              <div>
                <h2>{message.title}</h2>
                <time>{String(message.create_time)}</time>
              </div>
              <p>{message.content}</p>
            </div>
          </article>
        ))}
        {!query.isLoading && !query.data?.length && (
          <div className="empty-state">
            <Bell />
            <h2>{tr("No tienes mensajes nuevos")}</h2>
          </div>
        )}
      </section>
    </main>
  );
}

export function PointsPage() {
  const { user } = useSession();
  const { tr } = useCopy();
  const client = useQueryClient();
  const [mode, setMode] = useState<1 | 2>(1);
  const [amount, setAmount] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const config = useQuery({
    queryKey: ["exchange-config"],
    queryFn: engagementApi.exchangeConfig,
  });
  const records = useQuery({
    queryKey: ["point-records"],
    queryFn: engagementApi.pointRecords,
    enabled: showHistory,
  });
  const exchange = useMutation({
    mutationFn: () => engagementApi.exchange(mode, Number(amount)),
    onSuccess: () => {
      setAmount("");
      client.invalidateQueries({ queryKey: ["point-records"] });
      client.invalidateQueries({ queryKey: ["session-user"] });
    },
  });
  const numeric = Number(amount);
  const minimum =
    mode === 1
      ? config.data?.scoreToMoneyStart
      : config.data?.moneyToScoreStart;
  const estimated =
    mode === 1
      ? numeric / Number(config.data?.scoreRate || 1)
      : numeric * Number(config.data?.currencyRate || 1);
  return (
    <main className="page engagement-page">
      <PageHeader
        eyebrow="Puntos y beneficios"
        title="Centro de puntos"
        description="Convierte puntos y saldo con las tasas disponibles en tu cuenta."
        action={
          <button
            className="icon-action"
            aria-label={tr("Ver historial")}
            onClick={() => setShowHistory(!showHistory)}
          >
            <History />
          </button>
        }
      />
      <section className="points-balance">
        <div>
          <span>
            <Sparkles />
          </span>
          <small>{tr("Puntos disponibles")}</small>
          <strong>{user.score}</strong>
        </div>
        <div>
          <span>
            <Coins />
          </span>
          <small>{tr("Saldo disponible")}</small>
          <strong>{formatUsd(user.balance)}</strong>
        </div>
      </section>
      <section className="exchange-card">
        <div className="segmented">
          <button
            className={mode === 1 ? "active" : ""}
            onClick={() => {
              setMode(1);
              setAmount("");
            }}
          >
            {tr("Puntos a saldo")}
          </button>
          <button
            className={mode === 2 ? "active" : ""}
            onClick={() => {
              setMode(2);
              setAmount("");
            }}
          >
            {tr("Saldo a puntos")}
          </button>
        </div>
        <label className="field-label" htmlFor="exchange-amount">
          {tr(mode === 1 ? "Cantidad de puntos" : "Monto")}
        </label>
        <div className="money-input">
          <span>{mode === 1 ? tr("PTS") : "$"}</span>
          <input
            id="exchange-amount"
            type="number"
            inputMode="decimal"
            min={minimum || 1}
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            placeholder="0"
          />
        </div>
        <div className="exchange-rate">
          <span>{tr("Tasa actual")}</span>
          <strong>
            {mode === 1
              ? `${config.data?.scoreRate || "—"} ${tr("puntos")} = $1`
              : `$1 = ${config.data?.currencyRate || "—"} ${tr("puntos")}`}
          </strong>
        </div>
        {numeric > 0 && (
          <div className="exchange-result">
            <span>{tr("Recibirás aproximadamente")}</span>
            <strong>
              {mode === 1
                ? formatUsd(estimated)
                : `${estimated.toFixed(0)} ${tr("puntos")}`}
            </strong>
          </div>
        )}
        {exchange.isSuccess && (
          <div className="form-feedback success">
            <BadgeCheck /> {tr("Conversión completada.")}
          </div>
        )}
        {exchange.isError && (
          <div className="form-feedback error">
            <CircleAlert /> {exchange.error.message}
          </div>
        )}
        <button
          className="button primary full"
          disabled={
            !numeric || numeric < Number(minimum || 1) || exchange.isPending
          }
          onClick={() => exchange.mutate()}
        >
          {exchange.isPending ? (
            <LoaderCircle className="spin" />
          ) : (
            <RefreshCw />
          )}{" "}
          {tr("Convertir")}
        </button>
      </section>
      {showHistory && (
        <section className="point-history">
          <div className="section-heading">
            <div>
              <p className="eyebrow">{tr("Actividad")}</p>
              <h2>{tr("Historial de puntos")}</h2>
            </div>
          </div>
          {records.isLoading ? (
            <div className="loading-row">
              <LoaderCircle className="spin" /> {tr("Cargando historial…")}
            </div>
          ) : (
            records.data?.map((item, index) => {
              const delta =
                Number(item.after_amount) - Number(item.before_amount);
              return (
                <article key={`${item.create_time}-${index}`}>
                  <span className={delta >= 0 ? "credit" : "debit"}>
                    <Coins />
                  </span>
                  <div>
                    <strong>{tr(String(item.type))}</strong>
                    <small>{item.create_time}</small>
                  </div>
                  <b>
                    {delta >= 0 ? "+" : ""}
                    {delta}
                  </b>
                </article>
              );
            })
          )}
        </section>
      )}
    </main>
  );
}

export function ProfilePage() {
  const { user, setSession } = useSession();
  const { tr } = useCopy();
  const [name, setName] = useState(user.username);
  const [passwords, setPasswords] = useState({
    old: "",
    next: "",
    confirm: "",
  });
  const [visible, setVisible] = useState(false);
  const [avatarError, setAvatarError] = useState("");
  const nameMutation = useMutation({
    mutationFn: () => engagementApi.updateName(name),
    onSuccess: () => {
      setSession({ ...user, username: name });
    },
  });
  const passwordMutation = useMutation({
    mutationFn: () =>
      engagementApi.updatePassword(
        passwords.old,
        passwords.next,
        passwords.confirm,
      ),
    onSuccess: () => setPasswords({ old: "", next: "", confirm: "" }),
  });
  const avatarMutation = useMutation({
    mutationFn: async (file: File) => {
      await engagementApi.updateAvatar(file);
      return engagementApi.userDetails();
    },
    onSuccess: (raw) => {
      const value = String(raw.head_img_full || raw.head_img || "");
      const avatar = value
        ? value + (value.includes("?") ? "&" : "?") + "v=" + String(Date.now())
        : user.avatar;
      setSession({ ...user, avatar });
      setAvatarError("");
    },
  });
  const changeAvatar = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setAvatarError(tr("Selecciona una imagen JPG, PNG o WebP."));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError(tr("La imagen no puede superar 5 MB."));
      return;
    }
    setAvatarError("");
    avatarMutation.mutate(file);
  };
  const saveName = (event: FormEvent) => {
    event.preventDefault();
    nameMutation.mutate();
  };
  const savePassword = (event: FormEvent) => {
    event.preventDefault();
    if (passwords.next === passwords.confirm) passwordMutation.mutate();
  };
  return (
    <main className="page engagement-page">
      <PageHeader
        eyebrow="Seguridad de la cuenta"
        title="Perfil y acceso"
        description="Administra tu nombre visible y la contraseña de acceso."
      />
      <section className="profile-summary">
        <label
          className="profile-avatar"
          aria-label={tr("Cambiar foto")}
          title={tr("Cambiar foto")}
        >
          {user.avatar ? (
            <img src={user.avatar} alt={user.username} />
          ) : (
            <span>{user.username.slice(0, 1)}</span>
          )}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            disabled={avatarMutation.isPending}
            onChange={changeAvatar}
          />
          <i>
            {avatarMutation.isPending ? (
              <LoaderCircle className="spin" />
            ) : (
              <Camera />
            )}
          </i>
        </label>
        <div>
          <h2>{user.username}</h2>
          <p>ID {user.id}</p>
          <small>{user.phone || tr("Número no disponible")}</small>
        </div>
        <ShieldCheck />
      </section>
      {avatarMutation.isSuccess && (
        <div className="form-feedback success avatar-feedback">
          <BadgeCheck /> {tr("Foto actualizada.")}
        </div>
      )}
      {(avatarError || avatarMutation.isError) && (
        <div className="form-feedback error avatar-feedback">
          <CircleAlert /> {avatarError || avatarMutation.error?.message || ""}
        </div>
      )}
      <div className="settings-grid">
        <form className="settings-card" onSubmit={saveName}>
          <span className="settings-icon">
            <UserRound />
          </span>
          <div>
            <p className="eyebrow">{tr("Información personal")}</p>
            <h2>{tr("Nombre visible")}</h2>
          </div>
          <label className="field-label" htmlFor="profile-name">
            {tr("Nombre de usuario")}
          </label>
          <input
            id="profile-name"
            required
            minLength={2}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          {nameMutation.isSuccess && (
            <div className="form-feedback success">
              <BadgeCheck /> {tr("Nombre actualizado.")}
            </div>
          )}
          {nameMutation.isError && (
            <div className="form-feedback error">
              <CircleAlert /> {nameMutation.error.message}
            </div>
          )}
          <button
            className="button primary full"
            disabled={name === user.username || nameMutation.isPending}
          >
            {nameMutation.isPending ? (
              <LoaderCircle className="spin" />
            ) : (
              <Check />
            )}{" "}
            {tr("Guardar cambios")}
          </button>
        </form>
        <form className="settings-card" onSubmit={savePassword}>
          <span className="settings-icon">
            <LockKeyhole />
          </span>
          <div>
            <p className="eyebrow">{tr("Acceso")}</p>
            <h2>{tr("Cambiar contraseña")}</h2>
          </div>
          <label className="field-label">
            {tr("Contraseña actual")}
            <span className="input-shell">
              <LockKeyhole />
              <input
                type={visible ? "text" : "password"}
                required
                value={passwords.old}
                onChange={(event) =>
                  setPasswords({ ...passwords, old: event.target.value })
                }
              />
              <button
                type="button"
                aria-label={tr(
                  visible ? "Ocultar contraseña" : "Mostrar contraseña",
                )}
                onClick={() => setVisible(!visible)}
              >
                {visible ? <EyeOff /> : <Eye />}
              </button>
            </span>
          </label>
          <label className="field-label">
            {tr("Nueva contraseña")}
            <input
              type={visible ? "text" : "password"}
              required
              minLength={6}
              value={passwords.next}
              onChange={(event) =>
                setPasswords({ ...passwords, next: event.target.value })
              }
            />
          </label>
          <label className="field-label">
            {tr("Confirmar contraseña")}
            <input
              type={visible ? "text" : "password"}
              required
              minLength={6}
              value={passwords.confirm}
              onChange={(event) =>
                setPasswords({ ...passwords, confirm: event.target.value })
              }
            />
          </label>
          {passwords.confirm && passwords.next !== passwords.confirm && (
            <div className="form-feedback error">
              <CircleAlert /> {tr("Las contraseñas no coinciden.")}
            </div>
          )}
          {passwordMutation.isSuccess && (
            <div className="form-feedback success">
              <BadgeCheck /> {tr("Contraseña actualizada.")}
            </div>
          )}
          {passwordMutation.isError && (
            <div className="form-feedback error">
              <CircleAlert /> {passwordMutation.error.message}
            </div>
          )}
          <button
            className="button primary full"
            disabled={
              !passwords.old ||
              passwords.next.length < 6 ||
              passwords.next !== passwords.confirm ||
              passwordMutation.isPending
            }
          >
            {passwordMutation.isPending ? (
              <LoaderCircle className="spin" />
            ) : (
              <ShieldCheck />
            )}{" "}
            {tr("Actualizar contraseña")}
          </button>
        </form>
      </div>
    </main>
  );
}

export function SupportPage() {
  const { tr } = useCopy();
  const query = useQuery({
    queryKey: ["customer-service"],
    queryFn: engagementApi.customerService,
  });
  return (
    <main className="page engagement-page">
      <PageHeader
        eyebrow="Ayuda"
        title="Servicio al cliente"
        description="Comunícate con el equipo de soporte a través del canal oficial."
      />
      <section className="service-hero">
        <span>
          <Headphones />
        </span>
        <div>
          <p className="eyebrow light">GOP ALA Ecuador</p>
          <h2>{tr("¿En qué podemos ayudarte?")}</h2>
        </div>
        <a
          className="button sun"
          href={query.data?.url || "#"}
          target="_blank"
          rel="noreferrer"
          aria-disabled={!query.data?.url}
        >
          {query.isLoading ? (
            <LoaderCircle className="spin" />
          ) : (
            <MessageSquare />
          )}{" "}
          {tr("Abrir soporte")}
        </a>
      </section>
      <section className="support-topics">
        <article>
          <ShieldCheck />
          <div>
            <h2>{tr("Seguridad")}</h2>
          </div>
        </article>
        <article>
          <Coins />
          <div>
            <h2>{tr("Operaciones")}</h2>
          </div>
        </article>
        <article>
          <UserRound />
          <div>
            <h2>{tr("Cuenta")}</h2>
          </div>
        </article>
      </section>
    </main>
  );
}

export function DownloadPage() {
  const { tr } = useCopy();
  const query = useQuery({
    queryKey: ["download-urls"],
    queryFn: engagementApi.downloadUrls,
  });
  return (
    <main className="page engagement-page">
      <PageHeader
        eyebrow="Aplicación GOP ALA"
        title="Descargar aplicación"
        description="Elige la versión correspondiente a tu dispositivo."
      />
      <section className="download-hero">
        <div className="phone-frame">
          <span>
            <Smartphone />
          </span>
          <i />
          <i />
          <i />
        </div>
        <div>
          <p className="eyebrow light">GOP ALA Ecuador</p>
          <h2>{tr("Tu cuenta, siempre disponible")}</h2>
          <div className="download-actions">
            <a
              className="button sun"
              href={query.data?.android || "#"}
              target="_blank"
              rel="noreferrer"
              aria-disabled={!query.data?.android}
            >
              <Download /> Android
            </a>
            <a
              className="button secondary light-button"
              href={query.data?.ios || "#"}
              target="_blank"
              rel="noreferrer"
              aria-disabled={!query.data?.ios}
            >
              <Download /> iOS
            </a>
          </div>
        </div>
      </section>
      {query.isError && (
        <div className="form-feedback error">
          <CircleAlert /> {tr("No pudimos obtener los enlaces de descarga.")}
        </div>
      )}
    </main>
  );
}

export function RafflePage() {
  const { user } = useSession();
  const { tr } = useCopy();
  const client = useQueryClient();
  const [result, setResult] = useState<PrizeRecord | null>(null);
  const [rulesOpen, setRulesOpen] = useState(false);
  const prizes = useQuery({
    queryKey: ["prizes"],
    queryFn: engagementApi.prizes,
  });
  const winners = useQuery({
    queryKey: ["winners"],
    queryFn: engagementApi.winners,
  });
  const draw = useMutation({
    mutationFn: engagementApi.drawPrize,
    onSuccess: (prize) => {
      setResult(prize);
      client.invalidateQueries({ queryKey: ["winners"] });
      client.invalidateQueries({ queryKey: ["lottery-history"] });
      client.invalidateQueries({ queryKey: ["session-user"] });
    },
  });
  const slots = prizes.data
    ? [...prizes.data.slice(0, 4), null, ...prizes.data.slice(4, 8)]
    : [];
  return (
    <main className="page engagement-page raffle-page">
      <PageHeader
        eyebrow="Puntos y premios"
        title="Centro de premios"
        description="Participa y consulta los resultados recientes."
        action={
          <div className="title-actions">
            <button
              className="icon-action"
              aria-label={tr("Reglas")}
              onClick={() => setRulesOpen(true)}
            >
              <Info />
            </button>
            <Link
              className="icon-action"
              to="/rewards/raffle/history"
              aria-label={tr("Mi historial")}
            >
              <History />
            </Link>
            <Link
              className="icon-action"
              to="/rewards/points"
              aria-label={tr("Centro de puntos")}
            >
              <Coins />
            </Link>
          </div>
        }
      />
      <section className="raffle-assets">
        <div>
          <small>{tr("Saldo disponible")}</small>
          <strong>{formatUsd(user.balance)}</strong>
        </div>
        <div>
          <small>{tr("Puntos disponibles")}</small>
          <strong>{user.score}</strong>
        </div>
      </section>
      <section className="raffle-stage">
        <div className="raffle-heading">
          <span>
            <Crown />
          </span>
          <div>
            <p className="eyebrow light">GOP ALA Ecuador</p>
            <h2>{tr("Elige tu momento")}</h2>
          </div>
        </div>
        {prizes.isLoading && (
          <div className="loading-row raffle-loading">
            <LoaderCircle className="spin" /> {tr("Cargando…")}
          </div>
        )}
        {prizes.isError && (
          <div className="form-feedback error">
            <CircleAlert /> {tr("No pudimos cargar los premios.")}
          </div>
        )}
        <div className="prize-grid">
          {slots.map((prize, index) =>
            prize ? (
              <article key={prize.id}>
                <span>
                  {prize.icon_img_full || prize.icon_img ? (
                    <img
                      src={resolveMediaUrl(
                        prize.icon_img_full || prize.icon_img,
                      )}
                      alt=""
                      loading="lazy"
                    />
                  ) : String(prize.type) === "2" ? (
                    <Sparkles />
                  ) : String(prize.type) === "4" ? (
                    <Ticket />
                  ) : String(prize.type) === "3" ? (
                    <Coins />
                  ) : (
                    <Gift />
                  )}
                </span>
                <strong>{prizeLabel(prize, tr)}</strong>
              </article>
            ) : (
              <button
                key={`draw-${index}`}
                disabled={draw.isPending}
                onClick={() => draw.mutate()}
              >
                {draw.isPending ? (
                  <LoaderCircle className="spin" />
                ) : (
                  <Trophy />
                )}
                <strong>{tr("Participar")}</strong>
              </button>
            ),
          )}
        </div>
        {draw.isError && (
          <div className="form-feedback error">
            <CircleAlert /> {draw.error.message}
          </div>
        )}
        {result && (
          <div className="draw-result" role="status">
            <BadgeCheck />
            <div>
              <small>{tr("Resultado")}</small>
              <strong>{prizeLabel(result, tr)}</strong>
            </div>
            <button aria-label={tr("Cerrar")} onClick={() => setResult(null)}>
              ×
            </button>
          </div>
        )}
      </section>
      <section className="winner-card">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{tr("Actividad reciente")}</p>
            <h2>{tr("Últimos ganadores")}</h2>
          </div>
        </div>
        {winners.isLoading ? (
          <div className="loading-row">
            <LoaderCircle className="spin" /> {tr("Cargando…")}
          </div>
        ) : (
          <div className="winner-list">
            {winners.data?.map((winner, index) => (
              <article key={`${winner.id}-${index}`}>
                <span>
                  <Trophy />
                </span>
                <div>
                  <strong>
                    {winner.name || winner.phone || tr("Usuario GOP ALA")}
                  </strong>
                  <small>{winner.create_time}</small>
                </div>
                <b>{prizeLabel(winner, tr)}</b>
              </article>
            ))}
          </div>
        )}
      </section>
      <RemoteNoticeDialog
        open={rulesOpen}
        type={11}
        onClose={() => setRulesOpen(false)}
      />
    </main>
  );
}

export function LotteryHistoryPage() {
  const { isPreview } = useSession();
  const { tr } = useCopy();
  const pageSize = 10;
  const query = useInfiniteQuery({
    queryKey: ["lottery-history"],
    queryFn: ({ pageParam }) =>
      engagementApi.lotteryRecords(pageParam, pageSize),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage.length === pageSize ? pages.length + 1 : undefined,
    enabled: !isPreview,
  });
  const records = query.data?.pages.flat() || [];

  if (isPreview) {
    return (
      <main className="page engagement-page lottery-history-page">
        <Link className="back-link" to="/rewards/raffle">
          <ArrowLeft /> {tr("Volver al sorteo")}
        </Link>
        <div className="login-gate">
          <History />
          <h1>{tr("Mi historial")}</h1>
          <Link className="button primary" to="/auth/login">
            {tr("Iniciar sesión")}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page engagement-page lottery-history-page">
      <Link className="back-link" to="/rewards/raffle">
        <ArrowLeft /> {tr("Volver al sorteo")}
      </Link>
      <header className="page-title-row engagement-title">
        <div>
          <p className="eyebrow">{tr("Puntos y premios")}</p>
          <h1>{tr("Mi historial")}</h1>
        </div>
        <span className="title-icon">
          <History />
        </span>
      </header>

      {query.isLoading && (
        <div className="loading-row">
          <LoaderCircle className="spin" /> {tr("Cargando…")}
        </div>
      )}
      {query.isError && (
        <div className="form-feedback error">
          <CircleAlert /> {tr("No pudimos cargar el historial.")}
        </div>
      )}
      {!query.isLoading && !query.isError && records.length === 0 && (
        <div className="empty-state">
          <Gift />
          <h2>{tr("Aún no hay participaciones")}</h2>
        </div>
      )}
      {records.length > 0 && (
        <section className="lottery-history-list">
          {records.map((record, index) => (
            <article key={`${record.create_time}-${index}`}>
              <span className="lottery-record-icon">
                {record.icon_img_full || record.icon_img ? (
                  <img
                    src={record.icon_img_full || record.icon_img}
                    alt=""
                    loading="lazy"
                  />
                ) : (
                  <Gift />
                )}
              </span>
              <div>
                <strong>{lotteryLabel(record, tr)}</strong>
                <small>{record.create_time_r}</small>
              </div>
              <span className={`lottery-result type-${record.type}`}>
                {String(record.type) === "1"
                  ? tr("Sin premio")
                  : tr("Obtenido")}
              </span>
            </article>
          ))}
        </section>
      )}
      {query.hasNextPage && (
        <button
          className="button secondary full lottery-more"
          disabled={query.isFetchingNextPage}
          onClick={() => query.fetchNextPage()}
        >
          {query.isFetchingNextPage ? (
            <LoaderCircle className="spin" />
          ) : (
            <RefreshCw />
          )}
          {tr("Cargar más")}
        </button>
      )}
    </main>
  );
}
